import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, X, Check } from "lucide-react";

const BEREICHE = ["Brand", "Digital", "Print"];

// Public axios (no interceptor / no credentials header from other user)
const pub = axios.create({ baseURL: API });

export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const auth = useAuth();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [step, setStep] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [katalog, setKatalog] = useState({ leistungen: {}, tools: [] });
  const [f, setF] = useState({
    password: "",
    password2: "",
    telefon: "", adresse: "", geburtsjahr: "",
    portfolio_link: "", foto: "",
    stundensatz_standard: 60,
    bereiche: [], leistungen: [], tools: [], referenzbilder: [],
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await pub.get(`/designer-invites/${token}`);
        setInvite(data);
        setStep("form");
        // Try to load katalog (might need auth). We'll fall back if it fails
        try {
          const kk = await pub.get("/katalog");
          setKatalog(kk.data);
        } catch {
          // Katalog requires auth. Use minimal defaults.
          setKatalog({
            leistungen: {
              Brand: ["Markenstrategie", "Logo & Zeichen", "Corporate Design", "Styleguides", "Verpackungsdesign"],
              Digital: ["Webdesign", "Webflow-Entwicklung", "Shop & E-Commerce", "HTML5-Banner", "Social-Media-Grafik", "Motion & Animation"],
              Print: ["Editorial & Broschüren", "Geschäftsausstattung", "Plakat & Großformat", "Werbetechnik & Beschilderung", "Textil & Werbeartikel"],
            },
            tools: ["Figma", "Adobe InDesign", "Adobe Illustrator", "Adobe Photoshop", "Adobe After Effects", "Webflow", "Shopify"],
          });
        }
      } catch (e) {
        setErr(e.response?.data?.detail || "Einladung ungültig oder abgelaufen.");
        setStep("error");
      }
    })();
  }, [token]);

  const toggle = (key, val) => setF((s) => {
    const cur = new Set(s[key] || []); if (cur.has(val)) cur.delete(val); else cur.add(val);
    return { ...s, [key]: [...cur] };
  });

  const handleFoto = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader(); r.onload = () => setF((s) => ({ ...s, foto: r.result })); r.readAsDataURL(file);
  };
  const addRef = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader();
    r.onload = () => setF((s) => ({ ...s, referenzbilder: [...s.referenzbilder, r.result] }));
    r.readAsDataURL(file); e.target.value = "";
  };
  const removeRef = (i) => setF((s) => ({ ...s, referenzbilder: s.referenzbilder.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.password2) { setErr("Passwörter stimmen nicht überein."); return; }
    if (f.password.length < 8) { setErr("Passwort mindestens 8 Zeichen."); return; }
    setBusy(true); setErr("");
    try {
      const payload = {
        password: f.password,
        telefon: f.telefon, adresse: f.adresse,
        geburtsjahr: f.geburtsjahr ? parseInt(f.geburtsjahr, 10) : null,
        portfolio_link: f.portfolio_link, foto: f.foto,
        stundensatz_standard: parseFloat(f.stundensatz_standard) || 60,
        bereiche: f.bereiche, leistungen: f.leistungen, tools: f.tools, referenzbilder: f.referenzbilder,
      };
      const { data } = await pub.post(`/designer-invites/${token}/complete`, payload);
      if (data.token) localStorage.setItem("brunner_token", data.token);
      // Reload auth context by full reload of app
      window.location.href = "/";
    } catch (e) {
      setErr(e.response?.data?.detail || "Fehler beim Absenden.");
    } finally { setBusy(false); }
  };

  if (step === "loading") return <div className="min-h-screen flex items-center justify-center bg-paper mono-label">Lade…</div>;
  if (step === "error") return (
    <div className="min-h-screen flex items-center justify-center bg-paper p-8">
      <div className="paper-card p-8 max-w-md text-center">
        <h1 className="font-archivo font-bold text-2xl mb-2">Einladung ungültig</h1>
        <p className="text-sm text-ink/70">{err}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-paper py-10">
      <div className="max-w-3xl mx-auto px-6">
        <div className="mb-8 text-center">
          <div className="mono-label">Brunner Partner-Portal</div>
          <h1 className="font-archivo font-bold text-4xl mt-1">Willkommen, {invite.name}!</h1>
          <p className="text-ink/70 mt-2">Bitte ergänze dein Profil, damit wir dich für passende Projekte anfragen können.</p>
        </div>

        <form onSubmit={submit} className="paper-card p-8 space-y-6" data-testid="invite-form">
          <section>
            <h2 className="mono-label mb-3">Zugangsdaten</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="mono-label block mb-1">E-Mail</label>
                <input value={invite.email} disabled className="w-full px-3 py-2 border border-line rounded-sm bg-paper" />
              </div>
              <div />
              <div>
                <label className="mono-label block mb-1">Passwort (min. 8) *</label>
                <input required type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="inv-pw1" className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
              </div>
              <div>
                <label className="mono-label block mb-1">Passwort wiederholen *</label>
                <input required type="password" value={f.password2} onChange={(e) => setF({ ...f, password2: e.target.value })} data-testid="inv-pw2" className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
              </div>
            </div>
          </section>

          <section>
            <h2 className="mono-label mb-3">Kontakt & Foto</h2>
            <div className="flex gap-6 items-start">
              <div>
                <div className="w-24 h-24 rounded-sm border border-line overflow-hidden bg-paper flex items-center justify-center">
                  {f.foto ? <img src={f.foto} alt="" className="w-full h-full object-cover" /> : <span className="mono-label">Foto</span>}
                </div>
                <label className="mt-2 inline-flex items-center gap-1 text-xs cursor-pointer text-brunnerBlue hover:underline">
                  <Upload size={11} /> Foto wählen
                  <input type="file" accept="image/*" onChange={handleFoto} className="hidden" />
                </label>
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="mono-label block mb-1">Telefon</label>
                  <input value={f.telefon} onChange={(e) => setF({ ...f, telefon: e.target.value })} className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
                </div>
                <div>
                  <label className="mono-label block mb-1">Geburtsjahr</label>
                  <input type="number" value={f.geburtsjahr} onChange={(e) => setF({ ...f, geburtsjahr: e.target.value })} className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
                </div>
                <div className="md:col-span-2">
                  <label className="mono-label block mb-1">Adresse</label>
                  <input value={f.adresse} onChange={(e) => setF({ ...f, adresse: e.target.value })} className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
                </div>
                <div className="md:col-span-2">
                  <label className="mono-label block mb-1">Portfolio / Webseite</label>
                  <input value={f.portfolio_link} onChange={(e) => setF({ ...f, portfolio_link: e.target.value })} placeholder="https://…" className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
                </div>
                <div>
                  <label className="mono-label block mb-1">Stundensatz (€/h)</label>
                  <input type="number" step="0.5" value={f.stundensatz_standard} onChange={(e) => setF({ ...f, stundensatz_standard: e.target.value })} className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="mono-label mb-3">Bereiche</h2>
            <div className="flex flex-wrap gap-2">
              {BEREICHE.map((b) => {
                const on = f.bereiche.includes(b);
                return (
                  <button key={b} type="button" onClick={() => toggle("bereiche", b)} data-testid={`inv-bereich-${b}`}
                    className={`px-3 py-1.5 rounded-sm text-sm border ${on ? "bg-ink text-white border-ink" : "bg-white border-line hover:border-ink"}`}>{b}</button>
                );
              })}
            </div>
          </section>

          <section>
            <h2 className="mono-label mb-3">Leistungen</h2>
            {Object.entries(katalog.leistungen).map(([bereich, arr]) => (
              <div key={bereich} className="mb-3">
                <div className="mono-label mb-2" style={{opacity:1}}>{bereich}</div>
                <div className="flex flex-wrap gap-1.5">
                  {arr.map((l) => {
                    const on = f.leistungen.includes(l);
                    return (
                      <button key={l} type="button" onClick={() => toggle("leistungen", l)}
                        className={`text-xs px-2.5 py-1 rounded-sm border ${on ? "bg-ink text-white border-ink" : "bg-white border-line hover:border-ink"}`}>{l}</button>
                    );
                  })}
                </div>
              </div>
            ))}
          </section>

          <section>
            <h2 className="mono-label mb-3">Tools</h2>
            <div className="flex flex-wrap gap-1.5">
              {(katalog.tools || []).map((t) => {
                const on = f.tools.includes(t);
                return (
                  <button key={t} type="button" onClick={() => toggle("tools", t)}
                    className={`text-xs px-2.5 py-1 rounded-sm border ${on ? "bg-ink text-white border-ink" : "bg-white border-line hover:border-ink"}`}>{t}</button>
                );
              })}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="mono-label">Referenzen</h2>
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-line rounded-sm text-xs cursor-pointer hover:border-ink">
                <Upload size={11} /> Bild hinzufügen
                <input type="file" accept="image/*" onChange={addRef} className="hidden" />
              </label>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {f.referenzbilder.map((src, i) => (
                <div key={i} className="relative group">
                  <img src={src} alt="ref" className="w-full h-24 object-cover rounded-sm border border-line" />
                  <button type="button" onClick={() => removeRef(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1">
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {err && <div className="text-sm text-brunnerMagenta px-3 py-2 bg-brunnerMagenta/10 rounded-sm">{err}</div>}

          <div className="pt-4 border-t border-line flex justify-end">
            <button type="submit" disabled={busy} data-testid="inv-submit" className="inline-flex items-center gap-2 px-6 py-2.5 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60">
              <Check size={14} /> {busy ? "Speichern…" : "Profil absenden & anmelden"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
